// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { App } from "@capacitor/app";
import { isDefined } from "@clipboard-health/util-ts";
import { logError } from "@src/appV2/lib/analytics";
import { isEmpty } from "lodash";

/**
 * In order to consistently handle appUrlOpen events that fire immediately after the app is opened we need to register the listener at the top-level of a module.
 *
 * Our deepLinkHandler function is created from a hook on render but at that point we may have already missed the event, so we listen for it at the top-level and
 *   record any urls we see so that the handler can process them later.
 */
const appUrlOpenUrlQueue: string[] = [];
let appUrlOpenHandler: ((data: { url: string }) => void) | undefined;

App.addListener("appUrlOpen", async (data: { url: string | undefined }) => {
  const { url } = data;

  if (!isDefined(url) || isEmpty(url)) {
    return;
  }

  if (isDefined(appUrlOpenHandler)) {
    appUrlOpenHandler({ url });
    return;
  }

  appUrlOpenUrlQueue.push(url);
}).catch((error) => {
  logError("Error adding appUrlOpen listener", error);
});

export const setAppUrlOpenHandler = (handler: (data: { url: string }) => void) => {
  // Handles any appUrlOpen events that were queued up before a handler was set
  if (appUrlOpenUrlQueue.length > 0) {
    appUrlOpenUrlQueue.forEach((url) => handler({ url }));
    // Clear the queue so we don't process the urls again
    appUrlOpenUrlQueue.length = 0;
  }

  // Set the handler to be used for future appUrlOpen events
  appUrlOpenHandler = handler;
};
